import React from 'react';
import {
  extractTrailingPosition,
  getRecentFormCodeTooltip,
  isNumericFinishingPosition,
} from '../utils/recentFormCodes';
import './RecentFormInline.css';

type RecentFormInlineProps = {
  text: string | null | undefined;
  className?: string;
};

function FormCode({ code }: { code: string }): React.ReactElement {
  const trimmed = code.trim();
  if (!trimmed) {
    return <span>{code}</span>;
  }
  const tip = getRecentFormCodeTooltip(trimmed);
  return (
    <abbr className="recent-form-code" data-tooltip={tip} aria-label={tip} tabIndex={0}>
      {trimmed}
    </abbr>
  );
}

function renderSegment(segment: string, key: number): React.ReactNode {
  const { prefix, pos } = extractTrailingPosition(segment);
  if (pos !== null) {
    const posTrim = pos.trim();
    if (isNumericFinishingPosition(posTrim)) {
      return (
        <span key={key}>
          {prefix}
          {prefix ? ' ' : ''}({posTrim})
        </span>
      );
    }
    return (
      <span key={key}>
        {prefix}
        {prefix ? ' ' : ''}(
        <FormCode code={posTrim} />)
      </span>
    );
  }

  const whole = segment.trim();
  if (isNumericFinishingPosition(whole)) {
    return <span key={key}>{segment}</span>;
  }
  if (whole === '-' || whole === '—') {
    const dashTip = 'No finishing position recorded.';
    return (
      <abbr
        key={key}
        className="recent-form-code"
        data-tooltip={dashTip}
        aria-label={dashTip}
        tabIndex={0}
      >
        {segment}
      </abbr>
    );
  }
  return <FormCode key={key} code={whole} />;
}

export const RecentFormInline: React.FC<RecentFormInlineProps> = ({ text, className }) => {
  const raw = text?.trim() ?? '';
  if (!raw || raw === 'N/A') {
    return <span className={className}>{raw || 'N/A'}</span>;
  }

  const segments = raw.split(/,\s*/).filter((s) => s.trim().length > 0);
  const parts: React.ReactNode[] = [];
  segments.forEach((seg, i) => {
    if (i > 0) {
      parts.push(<span key={`sep-${i}`}>, </span>);
    }
    parts.push(renderSegment(seg, i));
  });

  return <span className={['recent-form-inline', className].filter(Boolean).join(' ')}>{parts}</span>;
};
